import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  GlobalStyle,
  Page,
  Container,
  Hero,
  Eyebrow,
  HeroTitle,
  HeroMeta,
  HeroLede,
  AcceptBanner,
  Layout,
  TocWrap,
  TocTitle,
  TocList,
  TocLink,
  Content,
  Section,
  SectionTitle,
  P,
  List,
  ListItem,
  CriticalCallout,
  CriticalLabel,
  ContactList,
  ContactRow,
  BackHome,
} from "./infoPageStyles";

const TOC = [
  { id: "commitment", label: "1. Our Commitment" },
  { id: "prohibited", label: "2. What's Prohibited" },
  { id: "age", label: "3. Keeping Minors Off NudR" },
  { id: "detection", label: "4. How We Detect Violations" },
  { id: "reporting", label: "5. How We Report CSAM" },
  { id: "report-to-us", label: "6. Reporting a Concern" },
  { id: "external", label: "7. External Resources" },
  { id: "enforcement", label: "8. Enforcement" },
  { id: "contact", label: "9. Child Safety Contact" },
];

export default function ChildSafetyPage() {
  const [active, setActive] = useState(TOC[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <GlobalStyle />

      <Hero>
        <Container>
          <Eyebrow>Safety</Eyebrow>
          <HeroTitle>Child Safety Standards</HeroTitle>
          <HeroMeta>
            <span>Effective Date: August 11, 2026</span>
          </HeroMeta>
          <HeroLede>
            NudR, operated by NUDR INC, is a strictly adults-only platform. This
            page sets out our standards against child sexual abuse and
            exploitation (CSAE), how we act on violations, how to report a
            concern, and how we work with the National Center for Missing &amp;
            Exploited Children (NCMEC) and law enforcement.
          </HeroLede>
          <AcceptBanner>
            NudR has zero tolerance for any content or conduct that sexualizes,
            exploits, or endangers a minor. Violations result in immediate
            permanent termination, evidence preservation, and reporting to NCMEC
            and law enforcement as required by law. There is no appeal.
          </AcceptBanner>
        </Container>
      </Hero>

      <Container>
        <Layout>
          <TocWrap>
            <TocTitle>On this page</TocTitle>
            <TocList>
              {TOC.map((item) => (
                <li key={item.id}>
                  <TocLink href={`#${item.id}`} $active={active === item.id}>
                    {item.label}
                  </TocLink>
                </li>
              ))}
            </TocList>
          </TocWrap>

          <Content>
            <Section id="commitment">
              <SectionTitle>
                <span>01</span>Our Commitment
              </SectionTitle>
              <P>
                NudR is intended solely for adults aged 18 and over. We do not
                knowingly permit anyone under 18 to create an account or use the
                app under any circumstances.
              </P>
              <P>
                We are committed to preventing the use of NudR for child sexual
                abuse or exploitation of any kind. These standards apply
                everywhere in the app — profiles, photographs, messages, shared
                media — and to off-platform conduct connected to a user we can
                identify.
              </P>
              <P>
                This page supplements our{" "}
                <Link to="/guidelines">Community Guidelines</Link>,{" "}
                <Link to="/terms">Terms of Service</Link>, and{" "}
                <Link to="/privacy">Privacy Policy</Link>.
              </P>
            </Section>

            <Section id="prohibited">
              <SectionTitle>
                <span>02</span>What's Prohibited
              </SectionTitle>
              <P>
                The following are strictly prohibited and result in immediate
                permanent termination:
              </P>
              <List>
                <ListItem>
                  <strong>Child sexual abuse material (CSAM)</strong> of any
                  kind — real, apparent, illustrated, animated,
                  computer-generated, or AI-generated.
                </ListItem>
                <ListItem>
                  <strong>Sexualization of minors</strong> — any content
                  presenting a person under 18 in a sexualized manner,
                  regardless of stated intent.
                </ListItem>
                <ListItem>
                  <strong>Grooming</strong> — any attempt to build a
                  relationship with a minor for the purpose of sexual
                  exploitation, including moving conversation off-platform,
                  isolating a minor from trusted adults, or arranging contact.
                </ListItem>
                <ListItem>
                  <strong>Solicitation</strong> — requesting sexual content from
                  a minor, or requesting or offering content involving minors.
                </ListItem>
                <ListItem>
                  <strong>Child sex trafficking</strong> — advertising,
                  facilitating, arranging, or attempting any commercial sexual
                  exploitation of a minor.
                </ListItem>
                <ListItem>
                  <strong>Attempting to identify or contact minors</strong>{" "}
                  through NudR for any purpose.
                </ListItem>
                <ListItem>
                  <strong>Minor self-sexualization.</strong> Where a person
                  under 18 creates or shares sexualized content of themselves,
                  we treat this as a child protection matter — not as a rule
                  violation by the minor.
                </ListItem>
              </List>
              <P>
                Attempting, soliciting, encouraging, or facilitating any of the
                above is itself a violation, whether or not the underlying act
                is completed.
              </P>
            </Section>

            <Section id="age">
              <SectionTitle>
                <span>03</span>Keeping Minors Off NudR
              </SectionTitle>
              <List>
                <ListItem>
                  <strong>Date of birth at signup.</strong> Users must provide a
                  date of birth and be at least 18 to register.
                </ListItem>
                <ListItem>
                  <strong>Identity verification.</strong> Users submit a
                  government-issued identification photograph, stored encrypted
                  and separately from the public profile, as described in our{" "}
                  <Link to="/privacy">Privacy Policy</Link>.
                </ListItem>
                <ListItem>
                  <strong>Action on discovery.</strong> If we learn an account
                  holder is under 18, we terminate the account and delete the
                  associated personal information.
                </ListItem>
                <ListItem>
                  <strong>Re-registration blocks.</strong> Accounts terminated
                  for age or child safety reasons have associated identity,
                  device, and payment signals blocked from signing up again.
                </ListItem>
              </List>
            </Section>

            <Section id="detection">
              <SectionTitle>
                <span>04</span>How We Detect Violations
              </SectionTitle>
              <P>
                We combine automated and human review to identify violating
                content:
              </P>
              <List>
                <ListItem>
                  Automated content-safety scanning of uploaded images,
                  including profile photographs and shared media.
                </ListItem>
                <ListItem>
                  Human review of content flagged automatically or reported by
                  users.
                </ListItem>
                <ListItem>
                  Review of reported messages. Message content is end-to-end
                  encrypted and unreadable to us in the ordinary course; when a
                  message is reported, that content is decrypted for safety
                  review, as described in our{" "}
                  <Link to="/terms">Terms of Service</Link>.
                </ListItem>
              </List>
              <P>
                Reports involving suspected child safety concerns are
                prioritised above every other report category.
              </P>
            </Section>

            <Section id="reporting">
              <SectionTitle>
                <span>05</span>How We Report CSAM
              </SectionTitle>
              <CriticalCallout>
                <CriticalLabel>Mandatory reporting</CriticalLabel>
                <P>
                  Where we obtain actual knowledge of apparent child sexual
                  abuse material, we report to the{" "}
                  <strong>
                    NCMEC CyberTipline as soon as reasonably possible
                  </strong>
                  , as required by 18 U.S.C. § 2258A, and preserve the reported
                  content as required by law. We also refer matters to law
                  enforcement where appropriate.
                </P>
              </CriticalCallout>
              <P>On identifying apparent CSAE content, we:</P>
              <List>
                <ListItem>
                  Remove the content and terminate the account immediately.
                </ListItem>
                <ListItem>
                  Preserve the content and account records in a secure,
                  access-restricted location.
                </ListItem>
                <ListItem>Submit a report to the NCMEC CyberTipline.</ListItem>
                <ListItem>
                  Cooperate with law enforcement follow-up, as described in our{" "}
                  <Link to="/law-enforcement">Law Enforcement Guidelines</Link>.
                </ListItem>
                <ListItem>
                  Block associated identity, device, and payment signals from
                  re-registration.
                </ListItem>
              </List>
              <P>
                We do not notify a user in advance of a child safety referral
                where doing so would risk destruction of evidence, harm to a
                child, or interference with an investigation.
              </P>
            </Section>

            <Section id="report-to-us">
              <SectionTitle>
                <span>06</span>Reporting a Concern to Us
              </SectionTitle>
              <P>
                If you encounter content or behaviour on NudR that you believe
                sexualizes, exploits, or endangers a minor, report it
                immediately. You can report directly in the app — see our{" "}
                <Link to="/safety-tools">Reporting &amp; Blocking</Link> page —
                or contact us:
              </P>
              <ContactList>
                <ContactRow>
                  <b>Child Safety Reports:</b>{" "}
                  <a href="mailto:childsafety@nudr.app">childsafety@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Trust &amp; Safety:</b>{" "}
                  <a href="mailto:security@nudr.app">security@nudr.app</a>
                </ContactRow>
              </ContactList>
              <P>
                Where possible, include the username or profile concerned, what
                you observed, and the approximate date and time.{" "}
                <strong>Do not download, save, forward, or attach</strong> any
                suspected child sexual abuse material — describe it instead.
                Possessing or transmitting such material is a criminal offence
                even when done with the intention of reporting it.
              </P>
            </Section>

            <Section id="external">
              <SectionTitle>
                <span>07</span>External Reporting Resources
              </SectionTitle>
              <CriticalCallout>
                <CriticalLabel>If a child is in immediate danger</CriticalLabel>
                <P>
                  <strong>Contact local emergency services first.</strong> In
                  the United States, call <strong>911</strong>. Then report to
                  us so we can preserve evidence and act on the account.
                </P>
              </CriticalCallout>
              <P>
                You can also report directly to these organisations,
                independently of us:
              </P>
              <List>
                <ListItem>
                  <strong>NCMEC CyberTipline</strong> (United States) —{" "}
                  <a
                    href="https://report.cybertip.org"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    report.cybertip.org
                  </a>{" "}
                  or 1-800-843-5678.
                </ListItem>
                <ListItem>
                  <strong>Internet Watch Foundation</strong> (UK and
                  international) —{" "}
                  <a
                    href="https://report.iwf.org.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    report.iwf.org.uk
                  </a>
                  .
                </ListItem>
                <ListItem>
                  <strong>INHOPE</strong> — network of national reporting
                  hotlines —{" "}
                  <a
                    href="https://www.inhope.org"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    inhope.org
                  </a>
                  .
                </ListItem>
              </List>
            </Section>

            <Section id="enforcement">
              <SectionTitle>
                <span>08</span>Enforcement
              </SectionTitle>
              <P>
                Violations of these standards bypass our standard enforcement
                ladder entirely. There is no warning, no temporary suspension,
                and <strong>no appeal</strong>.
              </P>
              <List>
                <ListItem>Immediate permanent account termination.</ListItem>
                <ListItem>Forfeiture of any prepaid amounts.</ListItem>
                <ListItem>
                  Evidence preservation and referral to NCMEC and law
                  enforcement.
                </ListItem>
                <ListItem>
                  Permanent block on re-registration across associated identity,
                  device, and payment signals.
                </ListItem>
              </List>
              <P>
                Knowingly submitting a false child safety report is itself a
                serious violation of our Community Guidelines.
              </P>
            </Section>

            <Section id="contact">
              <SectionTitle>
                <span>09</span>Child Safety Contact
              </SectionTitle>
              <P>
                For child safety matters, including enquiries from regulators,
                app marketplaces, child protection organisations, and law
                enforcement:
              </P>
              <ContactList>
                <ContactRow>
                  <b>Child Safety Point of Contact:</b>{" "}
                  <a href="mailto:childsafety@nudr.app">childsafety@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Legal / Law Enforcement:</b>{" "}
                  <a href="mailto:legal@nudr.app">legal@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Registered Address:</b> NUDR INC, 1105 N Buena Vista
                  Street, Burbank, CA 91505, U.S.A.
                </ContactRow>
              </ContactList>
              <P>
                For guidance on serving legal process, see our{" "}
                <Link to="/law-enforcement">Law Enforcement Guidelines</Link>.
              </P>

              <P style={{ marginTop: "1.4rem", fontSize: "0.85rem" }}>
                © {new Date().getFullYear()} NudR. All rights reserved.
              </P>

              <BackHome to="/">← Back to home</BackHome>
            </Section>
          </Content>
        </Layout>
      </Container>
    </Page>
  );
}
